"use client";
import matchStyles from "./TournamentMatchResults.module.css";
import {buildTeamForm,type TeamFormResult} from "@/lib/team-form";
import TournamentScorerFilters from "./TournamentScorerFilters";
import {filterTournamentScorers,emptyTournamentScorerFilter} from "@/lib/tournament-scorer-filters";
import type {FixtureCard} from "@/lib/fixture-data";

import { useEffect, useMemo, useState } from "react";
import Link from "next/link";

import PlayerLink from "./PlayerLink";
import PlayerGoalAuthors from "./PlayerGoalAuthors";
import TeamCrest from "@/components/TeamCrest";
import HeadToHead from "@/components/HeadToHead";
import TournamentMatrix from "@/components/TournamentMatrix";
import type { HeadToHeadMatch } from "@/lib/head-to-head";
import type { MatrixCell, StreakRecord, TableRow, TournamentStats } from "@/lib/football";

export type TeamView = {
  id: number;
  clubId?: number | null;
  name: string;
  short: string;
  colors: string[];
  logo: string;
  kitPattern: string;
  city: string;
};

export type MatchView = {
  id: number;
  homeTeamId: number;
  awayTeamId: number;
  home: string;
  away: string;
  homeGoals: number;
  awayGoals: number;
  round: number;
  dateLabel: string;
  dateTs: number | null;
  venue: string;
  timeLabel: string | null;
  detailsLabel: string;
  stage: string;
};

export type RoundView = {
  round: number;
  label?: string;
  dateLabel: string;
  matches: {
    id: number | null;
    home: string;
    away: string;
    homeTeamId: number;
    awayTeamId: number;
    homeGoals: number | null;
    awayGoals: number | null;
    technical?: boolean;
    technicalReason?: string;
    resultNote?: string;
    played: boolean;
    dateLabel: string;
    venue: string;
    timeLabel: string | null;
    detailsLabel: string;
    stage: string;
  }[];
};

export type ScorerView = {
  displayNote?: string;
  penalties?: number;
  unassignedPenalties?: number;
  id: number;
  playerName: string;
  team: string;
  goals: number;
  assists: number;
};

export type StageRow = {
  team: string;
  position: number;
  played: number;
  won: number;
  drawn: number;
  lost: number;
  goalsFor: number;
  goalsAgainst: number;
  goalDiff: number;
  points: number;
};

export type TournamentStageView = {
  id: "preliminary" | "A" | "B";
  label: string;
  subtitle: string;
  rows: StageRow[];
  matchesAvailable: boolean;
  matrixAvailable: boolean;
};

export type TournamentStageDataView = {
  withdrawnTeams?: string[];
  carryPreliminaryMatches?: boolean;
  enabled: boolean;
  stages: TournamentStageView[];
};

type Props = {
  cardDetails?:Record<number,FixtureCard[]>;
  goalDetails?: Record<number, { home: string; away: string }>;
  table: TableRow[];
  officialTable: boolean;
  teamList: TeamView[];
  matrix: MatrixCell[][];
  stats: TournamentStats;
  streaks: StreakRecord[];
  rounds: RoundView[];
  scorerList: ScorerView[];
  matchList: MatchView[];
  matchOrderKnown?: boolean;
  headToHeadMatches: HeadToHeadMatch[];
  currentTournamentId: number;
  /** Команда, выбранная через ссылку вида ?team= — открывает «Матчи» с фильтром. */
  initialTeam?: string;
  stageData?: TournamentStageDataView | null;
  stageMatrices?: Record<string, MatrixCell[][]>;
};

const TABS = [
  { id: "table", label: "Таблица", icon: "🏆" },
  { id: "calendar", label: "Матчи", icon: "📋" },
  { id: "matrix", label: "Шахматка", icon: "♟️" },
  { id: "h2h", label: "Очные встречи", icon: "⚔️" },
  { id: "scorers", label: "Бомбардиры", icon: "🥅" },
  { id: "stats", label: "Статистика и серии", icon: "📈" },
] as const;

type TabId = (typeof TABS)[number]["id"];

type FormResult = TeamFormResult;

const FORM_STYLE: Record<FormResult, { label: string; className: string }> = {
  W: { label: "В", className: "bg-emerald-400/25 text-emerald-200 ring-emerald-400/40" },
  D: { label: "Н", className: "bg-amber-400/25 text-amber-200 ring-amber-400/40" },
  L: { label: "П", className: "bg-rose-500/25 text-rose-200 ring-rose-400/40" },
};



/** Призёры: золото, серебро, бронза. */
const MEDALS: Record<number, { icon: string; row: string; chip: string; text: string }> = {
  1: {
    icon: "🥇",
    row: "border-l-4 border-l-[#ffd54a] bg-[#ffd54a]/[0.13]",
    chip: "bg-[#ffd54a] text-[#3f2d00]",
    text: "text-[#ffd54a]",
  },
  2: {
    icon: "🥈",
    row: "border-l-4 border-l-[#c9d2dc] bg-[#c9d2dc]/[0.13]",
    chip: "bg-[#c9d2dc] text-[#26313d]",
    text: "text-[#c9d2dc]",
  },
  3: {
    icon: "🥉",
    row: "border-l-4 border-l-[#b5773c] bg-[#b5773c]/[0.16]",
    chip: "bg-[#b5773c] text-[#2b1607]",
    text: "text-[#d59a5c]",
  },
};

function medalOf(position: number) {
  return MEDALS[position];
}

const SORT_COLUMNS = [
  { key: "position", label: "#", align: "text-left" },
  { key: "team", label: "Команда", align: "text-left" },
  { key: "played", label: "И", align: "text-center" },
  { key: "won", label: "В", align: "text-center" },
  { key: "drawn", label: "Н", align: "text-center" },
  { key: "lost", label: "П", align: "text-center" },
  { key: "goalsFor", label: "Забито", align: "text-center" },
  { key: "goalsAgainst", label: "Пропущено", align: "text-center" },
  { key: "goalDiff", label: "±", align: "text-center" },
  { key: "points", label: "О", align: "text-center" },
] as const;

type SortKey = (typeof SORT_COLUMNS)[number]["key"];

export default function TournamentTabs({
  table,
  officialTable,
  teamList,
  matrix,
  stats,
  streaks,
  rounds,
  scorerList,
  matchList,
  goalDetails = {},
  cardDetails = {},
  matchOrderKnown = true,
  headToHeadMatches,
  currentTournamentId,
  initialTeam,
  stageData,
  stageMatrices,
}: Props) {
  const [tab, setTab] = useState<TabId>(initialTeam ? "calendar" : "table");
  const [query, setQuery] = useState(initialTeam ?? "");
  const [sortKey, setSortKey] = useState<SortKey>("position");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [roundFilter, setRoundFilter] = useState<string>("all");
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const [highlightId, setHighlightId] = useState<number | null>(null);

  const hasStages = Boolean(stageData?.enabled && stageData.stages?.length >= 3);
  const [stageId, setStageId] = useState<"preliminary" | "A" | "B">("preliminary");
  const stageList = stageData?.stages ?? [];
  const activeStage = hasStages
    ? stageList.find((stage) => stage.id === stageId) ?? stageList[0]
    : null;
  const activeTable = activeStage && activeStage.id !== "preliminary"
    ? activeStage.rows.map((row) => {
        const normalizedStageName = row.team.toLocaleLowerCase("ru-RU").replace(/ё/g, "е");
        const team = teamList.find((item) => item.name.toLocaleLowerCase("ru-RU").replace(/ё/g, "е") === normalizedStageName)
          ?? (normalizedStageName === "снг" ? teamList.find((item) => item.name === "СаратовНефтеГаз") : undefined);
        return { ...row, teamId: team?.id ?? -row.position, team: row.team };
      })
    : table;
  const activeMatchList = activeStage && activeStage.id !== "preliminary"
    ? activeStage.matchesAvailable
      ? matchList.filter((match) => match.stage === activeStage.id || (stageData?.carryPreliminaryMatches && match.stage === "preliminary" && activeStage.rows.some((r) => r.team === match.home) && activeStage.rows.some((r) => r.team === match.away)))
      : []
    : hasStages ? matchList.filter((match) => match.stage === "preliminary") : matchList;
  const activeRounds = activeStage && activeStage.id !== "preliminary"
    ? activeStage.matchesAvailable
      ? rounds
          .map((round) => ({ ...round, matches: round.matches.filter((match) => match.stage === activeStage.id || (stageData?.carryPreliminaryMatches && match.stage === "preliminary" && activeStage.rows.some((r) => r.team === match.home) && activeStage.rows.some((r) => r.team === match.away))) }))
          .filter((round) => round.matches.length > 0)
      : []
    : hasStages
      ? rounds.map((round) => ({ ...round, matches: round.matches.filter((match) => match.stage === "preliminary") })).filter((round) => round.matches.length > 0)
      : rounds;
  const activeMatrix = activeStage
    ? (stageMatrices?.[activeStage.id] ?? (activeStage.id === "preliminary" ? matrix : []))
    : matrix;
  const stageHasMatches = !activeStage || activeStage.matchesAvailable;
  const stageHasMatrix = !activeStage || activeStage.matrixAvailable;

  const stageButtons = hasStages
    ? stageList
    : [];

  function StageSwitcher() {
    if (!hasStages) return null;
    return (
      <div className="flex flex-wrap gap-2">
        {stageButtons.map((stage) => (
          <button
            key={stage.id}
            type="button"
            onClick={() => setStageId(stage.id)}
            aria-pressed={stage.id === stageId}
            className={`rounded-full px-3 py-1.5 text-xs font-black uppercase tracking-wide transition active:scale-95 ${
              stage.id === stageId
                ? "bg-emerald-400 text-emerald-950"
                : "bg-emerald-400/10 text-emerald-200 ring-1 ring-emerald-400/20 hover:bg-emerald-400/20"
            }`}
          >
            {stage.label}
          </button>
        ))}
      </div>
    );
  }

  const teamNames = teamList.map((team) => team.name);

  // Прямые ссылки на вкладки и на игры работают также при переходе с очных встреч.
  useEffect(() => {
    let scrollTimer: number | undefined;
    let clearTimer: number | undefined;
    const openHash = () => {
      window.clearTimeout(scrollTimer);
      window.clearTimeout(clearTimer);
      const hash = window.location.hash;
      const match = hash.match(/^#match-(\d+)$/);
      if (match) {
        const id = Number(match[1]);
        setTab("calendar");
        setRoundFilter("all");
        setQuery("");
        setHighlightId(id);
        scrollTimer = window.setTimeout(() => {
          document.getElementById(`match-${id}`)?.scrollIntoView({
            behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
            block: "center",
          });
        }, 160);
        clearTimer = window.setTimeout(() => setHighlightId(null), 4000);
      } else {
        const target = TABS.find((item) => item.id === hash.slice(1));
        if (target) setTab(target.id);
      }
    };
    openHash();
    window.addEventListener("hashchange", openHash);
    return () => {
      window.clearTimeout(scrollTimer);
      window.clearTimeout(clearTimer);
      window.removeEventListener("hashchange", openHash);
    };
  }, []);

  const form = useMemo<Record<string, FormResult[]>>(() => buildTeamForm(teamNames,activeMatchList,matchOrderKnown), [teamNames, activeMatchList, matchOrderKnown]);

  const crest = useMemo(() => {
    const map = new Map<number, TeamView>();
    teamList.forEach((team) => map.set(team.id, team));
    return map;
  }, [teamList]);

  // Для предварительного этапа шахматка должна сохранять исходную нумерацию эталонного файла (1–15).
  // Для подгрупп А/Б порядок строк и столбцов — по местам соответствующей итоговой таблицы.
  const matrixOrder = useMemo(() => {
    const order = new Map(teamList.map((team, index) => [team.id, index]));
    if (activeStage?.id === "preliminary") {
      return teamList.map((team, index) => ({ team, index }));
    }
    return [...activeTable]
      .sort((x, y) => x.position - y.position)
      .map((row) => ({ team: crest.get(row.teamId), index: order.get(row.teamId) ?? 0 }));
  }, [activeStage?.id, activeTable, teamList, crest]);

  const sortedTable = useMemo(() => {
    return [...activeTable].sort((a, b) => {
      if (sortKey === "team") {
        return sortDir === "asc"
          ? a.team.localeCompare(b.team, "ru")
          : b.team.localeCompare(a.team, "ru");
      }
      const first = a[sortKey] as number;
      const second = b[sortKey] as number;
      return sortDir === "asc" ? first - second : second - first;
    });
  }, [activeTable, sortKey, sortDir]);

  const visibleRounds = useMemo(() => {
    const q = query.trim().toLowerCase();
    return activeRounds
      .filter((round) => roundFilter === "all" || String(round.round) === roundFilter)
      .map((round) => ({
        ...round,
        matches: q
          ? round.matches.filter(
              (match) =>
                match.home.toLowerCase().includes(q) || match.away.toLowerCase().includes(q),
            )
          : round.matches,
      }))
      .filter((round) => round.matches.length > 0);
  }, [activeRounds, roundFilter, query]);

  const [scorerFilter,setScorerFilter]=useState({...emptyTournamentScorerFilter});
  const sortedScorers = useMemo(
    () => filterTournamentScorers(scorerList,scorerFilter),
    [scorerList,scorerFilter],
  );

  const bestAttack = [...activeTable].sort((a, b) => b.goalsFor - a.goalsFor)[0];
  const bestDefence = [...activeTable].sort((a, b) => a.goalsAgainst - b.goalsAgainst)[0];

  async function copyMatchLink(matchId: number) {
    const url = `${window.location.origin}${window.location.pathname}#match-${matchId}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      window.prompt("Скопируйте ссылку на матч:", url);
    }
    setCopiedId(matchId);
    window.setTimeout(() => setCopiedId(null), 1800);
  }

  const inputClass =
    "rounded-full border border-emerald-400/25 bg-emerald-950/60 px-4 py-2 text-sm text-white outline-none placeholder:text-emerald-200/35 focus:border-emerald-300";

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap gap-2 pb-1">
        {TABS.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setTab(item.id)}
            aria-pressed={tab === item.id}
            className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-bold uppercase tracking-wide transition active:scale-95 ${
              tab === item.id
                ? "bg-emerald-400 text-emerald-950 shadow-[0_10px_25px_-12px_rgba(52,211,153,0.9)]"
                : "bg-emerald-400/10 text-emerald-200 ring-1 ring-emerald-400/20 hover:bg-emerald-400/20"
            }`}
          >
            <span className="mr-1.5">{item.icon}</span>
            {item.label}
          </button>
        ))}
      </div>

      {tab === "table" ? (
        <div className="glass-card overflow-hidden rounded-2xl">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-emerald-400/15 px-5 py-3">
            <div>
              <h2 className="text-lg font-black uppercase tracking-wide text-white">
{activeStage?.id === "preliminary" ? "Предварительный этап" : activeStage?.label ?? (officialTable ? "Итоговая таблица" : "Турнирная таблица")}
              </h2>
              <p className="text-xs text-emerald-200/50">
                {activeStage?.subtitle ?? (officialTable ? "перенесена из исходного файла" : "рассчитана по сыгранным матчам")}
              </p>
              <div className="mt-3"><StageSwitcher /></div>
            </div>
          </div>

          <div className="scrollbar-thin overflow-x-auto">
            <table className="tabular w-full min-w-[1020px] text-sm">
              <thead>
                <tr className="bg-emerald-950/60 text-[11px] uppercase tracking-wider text-emerald-200/70">
                  {SORT_COLUMNS.map((column) => (
                    <th key={column.key} className={`${column.align} px-3 py-2.5`}>
                      <button
                        type="button"
                        onClick={() => {
                          if (sortKey === column.key) {
                            setSortDir((dir) => (dir === "asc" ? "desc" : "asc"));
                          } else {
                            setSortKey(column.key);
                            setSortDir(column.key === "team" || column.key === "position" ? "asc" : "desc");
                          }
                        }}
                        className={`inline-flex items-center gap-1 transition hover:text-white ${
                          sortKey === column.key ? "text-emerald-300" : ""
                        }`}
                      >
                        {column.label}
                        <span className="text-[9px]">
                          {sortKey === column.key ? (sortDir === "asc" ? "▲" : "▼") : "↕"}
                        </span>
                      </button>
                    </th>
                  ))}
                  <th className="px-3 py-2.5 text-center">Форма</th>
                </tr>
              </thead>
              <tbody>
                {sortedTable.map((row) => {
                  const team = crest.get(row.teamId);
                  return (
                    <tr
                      key={row.teamId}
                      className={`${medalOf(row.position)?.row ?? "border-l-4 border-l-transparent"} border-b border-emerald-400/5 transition hover:bg-emerald-400/5`}
                    >
                      <td className="px-3 py-2.5">
                        {medalOf(row.position) ? (
                          <span
                            className="inline-flex h-6 w-6 items-center justify-center text-lg leading-none"
                            title={
                              row.position === 1
                                ? "Золото"
                                : row.position === 2
                                  ? "Серебро"
                                  : "Бронза"
                            }
                          >
                            {medalOf(row.position)!.icon}
                          </span>
                        ) : (
                          <span className="font-bold text-emerald-200/70">{row.position}</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5">
                        <span className="flex items-center gap-2.5 font-bold text-white">
                          {team ? (
                            <TeamCrest
                              name={team.name}
                              short={team.short}
                              colors={team.colors}
                              logo={team.logo}
                              pattern={team.kitPattern}
                              size={24}
                            />
                          ) : null}
                          <span className="min-w-0">
                            {team?.clubId ? (
                              <Link href={`/clubs/${team.clubId}`} className="block truncate hover:text-emerald-300 hover:underline" title="Общая статистика клуба во всех турнирах">{row.team}</Link>
                            ) : <span className="block truncate">{row.team}</span>}
                            {stageData?.withdrawnTeams?.includes(row.team)?<span className="block text-xs text-amber-200">Снята с турнира</span>:null}
                            {team?.city ? (
                              <span className="block truncate text-[11px] font-medium text-emerald-200/45">
                                {team.city}
                              </span>
                            ) : null}
                          </span>
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-center text-emerald-100/80">{row.played}</td>
                      <td className="px-3 py-2.5 text-center text-emerald-300">{row.won}</td>
                      <td className="px-3 py-2.5 text-center text-amber-200">{row.drawn}</td>
                      <td className="px-3 py-2.5 text-center text-rose-300">{row.lost}</td>
                      <td className="px-3 py-2.5 text-center text-emerald-100/80">{row.goalsFor}</td>
                      <td className="px-3 py-2.5 text-center text-emerald-100/60">
                        {row.goalsAgainst}
                      </td>
                      <td
                        className={`px-3 py-2.5 text-center font-semibold ${
                          row.goalDiff > 0
                            ? "text-emerald-300"
                            : row.goalDiff < 0
                              ? "text-rose-300"
                              : "text-emerald-100/60"
                        }`}
                      >
                        {row.goalDiff > 0 ? `+${row.goalDiff}` : row.goalDiff}
                      </td>
                      <td className="px-3 py-2.5 text-center text-base font-black text-white">
                        {row.points}
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center justify-center gap-1">
                          {(form[row.team] ?? []).length ? (
                            (form[row.team] ?? []).map((result, index) => (
                              <span
                                key={`${row.team}-${index}`}
                                title={
                                  result === "W" ? "Победа" : result === "D" ? "Ничья" : "Поражение"
                                }
                                className={`flex h-5 w-5 items-center justify-center rounded-md text-[10px] font-black ring-1 ${FORM_STYLE[result].className}`}
                              >
                                {FORM_STYLE[result].label}
                              </span>
                            ))
                          ) : (
                            <span className="text-emerald-200/25">—</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {!sortedTable.length ? (
                  <tr>
                    <td colSpan={11} className="px-5 py-8 text-center text-emerald-200/50">
                      Команды не найдены
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {tab === "calendar" ? (
        <div className="space-y-4">
          {hasStages ? (
            <div className="glass-card rounded-2xl p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h2 className="text-lg font-black uppercase tracking-wide text-white">Матчи</h2>
                  <p className="text-xs text-emerald-200/50">{activeStage?.subtitle}</p>
                </div>
                <StageSwitcher />
              </div>
            </div>
          ) : null}
          {!stageHasMatches ? (
            <div className="glass-card rounded-2xl px-5 py-10 text-center">
              <p className="text-sm font-bold text-emerald-100/75">Для этого этапа в предоставленных CSV есть итоговая таблица, но нет результатов отдельных матчей.</p>
              <p className="mt-2 text-xs text-emerald-200/45">Загрузим матчи второго этапа отдельно — после появления файла с результатами А/Б.</p>
            </div>
          ) : null}
          {stageHasMatches ? <div className="glass-card space-y-3 rounded-2xl p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="text-lg font-black uppercase tracking-wide text-white">
                {matchOrderKnown ? "Матчи по турам" : "Матчи по кругам"}
              </h2>
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Поиск по команде…"
                className={inputClass}
              />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-bold uppercase tracking-wider text-emerald-200/50">
                Тур:
              </span>
              <button
                type="button"
                onClick={() => setRoundFilter("all")}
                className={`rounded-full px-3 py-1.5 text-xs font-bold transition active:scale-95 ${
                  roundFilter === "all"
                    ? "bg-emerald-400 text-emerald-950"
                    : "bg-emerald-400/10 text-emerald-200 ring-1 ring-emerald-400/20 hover:bg-emerald-400/20"
                }`}
              >
                Все
              </button>
              {activeRounds.map((round) => (
                <button
                  key={round.round}
                  type="button"
                  onClick={() => setRoundFilter(String(round.round))}
                  className={`rounded-full px-3 py-1.5 text-xs font-bold transition active:scale-95 ${
                    roundFilter === String(round.round)
                      ? "bg-emerald-400 text-emerald-950"
                      : "bg-emerald-400/10 text-emerald-200 ring-1 ring-emerald-400/20 hover:bg-emerald-400/20"
                  }`}
                >
                  {round.label ?? `${round.round}-й тур`}
                </button>
              ))}
            </div>
          </div> : null}

          {stageHasMatches ? (
            <>
              {visibleRounds.map((round) => (
            <div key={round.round} className="glass-card overflow-hidden rounded-2xl">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-emerald-400/15 px-5 py-3">
                <h3 className="text-sm font-black uppercase tracking-wider text-white">
                  {round.label ?? `${round.round}-й тур`}
                </h3>

              </div>
              <ul className="divide-y divide-emerald-400/5">
                {round.matches.map((match, index) => {
                  const hasDetails = Boolean(match.detailsLabel);
                  return (
                  <li
                    key={`${match.homeTeamId}-${match.awayTeamId}-${index}`}
                    id={match.id ? `match-${match.id}` : undefined}
                    className={`${matchStyles.row} scroll-mt-28 px-3 py-3 text-sm transition sm:px-5 ${
                      match.id !== null && match.id === highlightId
                        ? "bg-emerald-400/20 ring-1 ring-inset ring-emerald-300/60"
                        : "hover:bg-emerald-400/5"
                    }`}
                  >
                    <span
                      className={`col-start-2 row-start-1 min-w-0 truncate text-right ${
                        match.played && (match.homeGoals ?? 0) > (match.awayGoals ?? 0)
                          ? "font-bold text-white"
                          : "text-emerald-100/70"
                      }`}
                    >
                      {match.home}
                    </span>
                    <span
                      className={`tabular col-start-3 row-start-1 w-full rounded-lg px-3 py-1 text-center font-black ring-1 ${
                        match.played
                          ? "bg-emerald-950/80 text-emerald-300 ring-emerald-400/20"
                          : "bg-emerald-400/5 text-emerald-200/40 ring-emerald-400/10"
                      }`}
                    >
                      {match.played ? `${match.homeGoals} : ${match.awayGoals}` : "— : —"}
                      {match.technical?<span className="block text-[10px] font-bold text-amber-300" title={match.technicalReason||"Техническое поражение"} aria-label={match.technicalReason?`Техническое поражение: ${match.technicalReason}`:"Техническое поражение"}>ТП</span>:null}
                    </span>
                    <span
                      className={`col-start-4 row-start-1 min-w-0 truncate ${
                        match.played && (match.awayGoals ?? 0) > (match.homeGoals ?? 0)
                          ? "font-bold text-white"
                          : "text-emerald-100/70"
                      }`}
                    >
                      {match.away}
                    </span>
                    {hasDetails || match.resultNote || (match.technical && match.technicalReason) ? (
                      <span className="col-span-3 col-start-2 row-start-2 min-w-0 text-center text-[11px] leading-snug text-emerald-200/50">
                        {match.detailsLabel}
                        {match.resultNote?<span className="block text-emerald-100/80">{match.resultNote}</span>:null}
                        {match.technical&&match.technicalReason?<span className="block text-amber-200">{match.technicalReason}</span>:null}
                      </span>
                    ) : null}
                    {match.id !== null && goalDetails[match.id] ? (
                      <div className={`${matchStyles.details} row-start-3 text-[11px] leading-relaxed text-emerald-100/70`}>
                        <span className={matchStyles.homeDetails}><PlayerGoalAuthors text={goalDetails[match.id].home} team={match.home} scorers={scorerList}/></span>
                        <span className={matchStyles.awayDetails}><PlayerGoalAuthors text={goalDetails[match.id].away} team={match.away} scorers={scorerList}/></span>
                      </div>
                    ) : null}
                    {match.id!==null&&cardDetails[match.id]?.length ? (
                      <div className={`${matchStyles.details} row-start-4 text-xs leading-relaxed text-emerald-100/70`}>
                        {[match.homeTeamId,match.awayTeamId].map((teamId,index)=>(
                          <div key={teamId} className={index===0?matchStyles.homeDetails:matchStyles.awayDetails}>
                            {cardDetails[match.id!].filter(card=>card.teamId===teamId).map((card,n)=>(
                              <span key={n} className={matchStyles.card}>
                                <span aria-label={card.type==="yellow"?"Жёлтая карточка":"Красная карточка"}>{card.type==="yellow"?"🟨":"🟥"}</span>{" "}
                                <PlayerLink name={card.playerName} team={index===0?match.home:match.away} tournamentId={currentTournamentId}>{card.playerName}</PlayerLink>
                                {card.minute!==null?` ${card.minute}′`:""}
                              </span>
                            ))}
                          </div>
                        ))}
                      </div>
                    ) : null}
                    {match.played && match.id !== null ? (
                      <button
                        type="button"
                        onClick={() => copyMatchLink(match.id as number)}
                        title="Скопировать ссылку на матч"
                        aria-label={`Скопировать ссылку на матч ${match.home} — ${match.away}`}
                        className={`col-start-5 row-start-1 w-full self-center justify-self-end whitespace-nowrap rounded-full border border-emerald-400/25 px-2.5 py-1 text-xs font-semibold text-emerald-200 transition hover:bg-emerald-400/10 active:scale-95 ${
                          hasDetails ? "row-span-2" : ""
                        }`}
                      >
                        {copiedId === match.id ? "✓" : "🔗"}
                        <span className="hidden sm:inline">
                          {copiedId === match.id ? " Готово" : " Ссылка"}
                        </span>
                      </button>
                    ) : (
                      <span
                        className={`col-start-5 row-start-1 w-full ${hasDetails ? "row-span-2" : ""}`}
                        aria-hidden="true"
                      />
                    )}
                  </li>
                  );
                })}
              </ul>
            </div>
              ))}

          {!visibleRounds.length ? (
            <div className="glass-card rounded-2xl px-5 py-8 text-center text-emerald-200/50">
              Матчи не найдены
            </div>
          ) : null} 
            </>
          ) : null}
        </div>
      ) : null}

      {tab === "matrix" ? (
        <div className="space-y-4">
          {hasStages ? (
            <div className="glass-card rounded-2xl p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h2 className="text-lg font-black uppercase tracking-wide text-white">Шахматка</h2>
                  <p className="text-xs text-emerald-200/50">{activeStage?.subtitle}</p>
                </div>
                <StageSwitcher />
              </div>
            </div>
          ) : null}
          {stageHasMatrix ? <TournamentMatrix order={matrixOrder} matrix={activeMatrix} /> : (
            <div className="glass-card rounded-2xl px-5 py-10 text-center">
              <p className="text-sm font-bold text-emerald-100/75">Для этого этапа в предоставленных CSV нет шахматки результатов.</p>
              <p className="mt-2 text-xs text-emerald-200/45">Сейчас загружена только итоговая таблица подгруппы.</p>
            </div>
          )}
        </div>
      ) : null}

      {tab === "h2h" ? (
        <HeadToHead teams={teamList} matches={headToHeadMatches} currentTournamentId={currentTournamentId} />
      ) : null}

      {tab === "scorers" ? (
        <div className="glass-card overflow-hidden rounded-2xl">
          <div className="border-b border-emerald-400/15 px-5 py-3">
            <h2 className="text-lg font-black uppercase tracking-wide text-white">
              Бомбардирская таблица
            </h2>
          </div>
          <TournamentScorerFilters value={scorerFilter} onChange={setScorerFilter} teams={[...new Set(scorerList.map(r=>r.team))].sort((a,b)=>a.localeCompare(b,"ru"))} assists/>
          {sortedScorers.length ? (
            <div className="scrollbar-thin overflow-x-auto">
              <table className="tabular w-full min-w-[520px] text-sm">
                <thead>
                  <tr className="bg-emerald-950/60 text-[11px] uppercase tracking-wider text-emerald-200/70">
                    <th className="px-3 py-2.5 text-left">#</th>
                    <th className="px-3 py-2.5 text-left">Игрок</th>
                    <th className="px-3 py-2.5 text-left">Команда</th>
                    <th className="px-3 py-2.5 text-center">Голы (с пенальти)</th>
                    <th className="px-3 py-2.5 text-center">Пасы</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedScorers.map((scorer, index) => (
                    <tr
                      key={scorer.id}
                      className="border-b border-emerald-400/5 transition hover:bg-emerald-400/5"
                    >
                      <td className="px-3 py-2.5 font-bold text-emerald-200/70">{index + 1}</td>
                      <td className="px-3 py-2.5 font-bold text-white">
                        {index === 0 ? "⚽ " : ""}
                        <PlayerLink id={scorer.id} name={scorer.playerName}/>
                      </td>
                      <td className="px-3 py-2.5 text-emerald-100/70"><span className="inline-flex items-center gap-2"><TeamCrest name={scorer.team} logo={teamList.find(t=>t.name===scorer.team)?.logo} colors={teamList.find(t=>t.name===scorer.team)?.colors} pattern={teamList.find(t=>t.name===scorer.team)?.kitPattern} size={28}/>{scorer.team}</span></td>
                      <td className="px-3 py-2.5 text-center text-base font-black text-emerald-300">
                        {scorer.goals}{(scorer.penalties??0)>0?` (${scorer.penalties})`:""}
                      </td>
                      <td className="px-3 py-2.5 text-center text-emerald-100/60">
                        {scorer.assists}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="px-5 py-10 text-center">
              <p className="text-4xl">🥅</p>
              <p className="mt-2 text-sm text-emerald-200/60">
                {scorerList.length?"По выбранным фильтрам бомбардиры не найдены.":"Данные по бомбардирам этого турнира пока не загружены."}
              </p>
            </div>
          )}
        </div>
      ) : null}

      {tab === "stats" ? (
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { label: "Сыграно матчей", value: stats.matchesPlayed, icon: "📋" },
              { label: "Забито голов", value: stats.goals, icon: "🥅" },
              { label: "В среднем за матч", value: stats.avgGoals, icon: "📈" },
              { label: "Ничьих", value: stats.draws, icon: "🤝" },
              { label: "Матчей «на ноль»", value: stats.cleanSheets, icon: "🧤" },
              { label: "Команд", value: teamNames.length, icon: "🛡️" },
            ].map((item) => (
              <div
                  key={item.label}
                  className="glass-card rounded-2xl p-5 text-center transition hover:-translate-y-0.5 hover:border-emerald-300/30"
                >
                  <p className="text-center text-xs font-bold uppercase tracking-wider text-emerald-200/50">
                    {item.icon} {item.label}
                  </p>
                  <p className="tabular mt-2 text-center text-3xl font-black text-white">{item.value}</p>
                </div>
            ))}
          </div>

          <div className="glass-card rounded-2xl p-5">
            <h3 className="text-sm font-black uppercase tracking-wider text-emerald-300">
              🔥 Длинные серии
            </h3>
            <ul className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {streaks.map((streak) => (
                <li
                  key={streak.kind}
                  className="rounded-2xl bg-emerald-950/50 px-4 py-4 text-center ring-1 ring-emerald-400/10"
                >
                  <p className="text-center text-[11px] font-bold uppercase tracking-wider text-emerald-200/50">
                    {streak.label}
                  </p>
                  <p className="tabular mt-1 text-center text-3xl font-black text-emerald-300">
                    {streak.count}
                  </p>
                  <p className="mt-1 truncate text-center text-sm font-bold text-white">{streak.team}</p>
                </li>
              ))}
              {!streaks.length ? (
                <li className="text-sm text-emerald-200/50">{matchOrderKnown ? "Недостаточно матчей для серий." : "Порядок матчей в исходнике не указан. Достоверно рассчитать серии нельзя."}</li>
              ) : null}
            </ul>
          </div>

          <div className="glass-card rounded-2xl p-5">
            <h3 className="text-sm font-black uppercase tracking-wider text-emerald-300">
              Рекорды турнира
            </h3>
            <dl className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <dt className="text-xs uppercase tracking-wider text-emerald-200/50">
                  Крупнейшая победа
                </dt>
                <dd className="mt-1 text-sm font-bold text-white">
                  {stats.biggestWin ? `${stats.biggestWin.label} — ${stats.biggestWin.score}` : "—"}
                </dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wider text-emerald-200/50">
                  Самый результативный матч
                </dt>
                <dd className="mt-1 text-sm font-bold text-white">
                  {stats.topScoringMatch
                    ? `${stats.topScoringMatch.label} — ${stats.topScoringMatch.score}`
                    : "—"}
                </dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wider text-emerald-200/50">Лучшая атака</dt>
                <dd className="mt-1 text-sm font-bold text-white">
                  {bestAttack ? `${bestAttack.team} — ${bestAttack.goalsFor} гола(ов)` : "—"}
                </dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wider text-emerald-200/50">
                  Лучшая оборона
                </dt>
                <dd className="mt-1 text-sm font-bold text-white">
                  {bestDefence ? `${bestDefence.team} — ${bestDefence.goalsAgainst} пропущено` : "—"}
                </dd>
              </div>
            </dl>
          </div>
        </div>
      ) : null}
    </div>
  );
}
